import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LogOut, MapPin, Wrench, Clock, CheckCircle2, XCircle, Coffee, AlertCircle, Award, Zap, BookOpen, Calendar, Car, ClipboardList, Play, CheckCircle, CalendarDays, Wallet, TrendingUp, BarChart3, Bell } from "lucide-react";
import BenefitsModal from "./BenefitsModal";
import MyAttendanceDialog from "@/components/worker/MyAttendanceDialog";
import WorkerMessenger from "@/components/worker/WorkerMessenger";
import { formatDateRO, formatDateTimeRO } from "@/utils/dateFormat";
import GlassLayout from "@/components/GlassLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get user from either storage
function getStoredUser() {
  const fromLocal = localStorage.getItem('user');
  const fromSession = sessionStorage.getItem('user');
  const raw = fromLocal || fromSession;
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (error) {
    console.error('Failed to parse stored user:', error);
    return null;
  }
}

export default function WorkerDashboard() {
  const navigate = useNavigate();
  const user = getStoredUser() || {};
  const companyId = user.company_id; // Multi-tenancy
  
  const [status, setStatus] = useState({ checked_in: false, attendance: null, auto_generated: null });
  const [sites, setSites] = useState([]);
  const [tools, setTools] = useState([]);
  const [companyVehicles, setCompanyVehicles] = useState([]);
  const [compensatoryHours, setCompensatoryHours] = useState(0);
  const [overtimeHours, setOvertimeHours] = useState(0);
  const [myTasks, setMyTasks] = useState([]);
  const [activeTask, setActiveTask] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState(null); // Statistici lunare (ore + câștiguri)
  const [myNotifications, setMyNotifications] = useState([]);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const [showNotifDialog, setShowNotifDialog] = useState(false);
  
  const [showCheckinDialog, setShowCheckinDialog] = useState(false);
  const [showBreakDialog, setShowBreakDialog] = useState(false);
  const [showBenefitsModal, setShowBenefitsModal] = useState(false);
  const [showMyAttendance, setShowMyAttendance] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showCompleteTaskDialog, setShowCompleteTaskDialog] = useState(false);
  const [taskResolutionNotes, setTaskResolutionNotes] = useState("");
  
  const [selectedSite, setSelectedSite] = useState("");
  const [selectedTools, setSelectedTools] = useState([]);
  const [location, setLocation] = useState(null);
  
  // Company car state
  const [usesCompanyCar, setUsesCompanyCar] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [startKm, setStartKm] = useState("");
  const [activeTripSheet, setActiveTripSheet] = useState(null);
  const [tripRoute, setTripRoute] = useState(null);
  
  const [breakDuration, setBreakDuration] = useState(30);
  const [breakReason, setBreakReason] = useState("");
  
  const [leaveType, setLeaveType] = useState("CO");
  const [leaveStartDate, setLeaveStartDate] = useState("");
  const [leaveEndDate, setLeaveEndDate] = useState("");
  const [leaveReason, setLeaveReason] = useState("");
  const [leaveDays, setLeaveDays] = useState(0);
  
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(getCurrentTime());

  // Set document title for Worker Dashboard
  // Set document title for Worker Dashboard
  useEffect(() => {
    document.title = `e-Tesa - ${user.name || "Angajat"}`;
    return () => {
      document.title = "e-Tesa";
    };
  }, [user.name]);

  // Handle browser back button - prevent logout
  useEffect(() => {
    const handleBackButton = () => {
      window.history.pushState(null, "");
    };
    window.history.pushState(null, "");
    window.addEventListener("popstate", handleBackButton);
    return () => window.removeEventListener("popstate", handleBackButton);
  }, []);

  function getCurrentTime() {
    const now = new Date();
    return now.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  // GPS location disabled temporarily
  const getLocation = () => {
    return Promise.resolve({ latitude: null, longitude: null });
  };

  // Define all fetch functions with useCallback BEFORE the useEffect that uses them
  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/attendance/status/${user.id}`);
      setStatus(response.data);
      
      // Show edit dialog if auto-generated attendance exists
      if (response.data.auto_generated && !response.data.auto_generated.edited_by_worker) {
        setShowEditDialog(true);
      }
    } catch (error) {
      console.error("Error fetching status:", error);
    }
  }, [user.id]);

  const fetchSites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sites`, { params: { company_id: companyId } });
      setSites(response.data);
    } catch (error) {
      console.error("Error fetching sites:", error);
    }
  }, [companyId]);

  const fetchTools = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tools`, { params: { company_id: companyId } });
      setTools(response.data);
    } catch (error) {
      console.error("Error fetching tools:", error);
    }
  }, [companyId]);

  const fetchCompanyVehicles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/company-vehicles`, { params: { company_id: companyId } });
      setCompanyVehicles(response.data);
    } catch (error) {
      console.error("Error fetching company vehicles:", error);
    }
  }, [companyId]);

  const fetchMyTasks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tasks/my/${user.id}`, { params: { company_id: companyId } });
      setMyTasks(response.data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  }, [user.id, companyId]);

  const fetchMyNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications`, { params: { user_id: user.id, company_id: companyId } });
      setMyNotifications(response.data);
      setUnreadNotifCount(response.data.filter(n => !n.read).length);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, [user.id, companyId]);

  const fetchCompensatory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/compensatory/${user.id}`);
      setCompensatoryHours(response.data.compensatory_hours);
    } catch (error) {
      console.error("Error fetching compensatory:", error);
    }
  }, [user.id]);

  const fetchOvertime = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/overtime/${user.id}`);
      setOvertimeHours(response.data.overtime_hours);
    } catch (error) {
      console.error("Error fetching overtime:", error);
    }
  }, [user.id]);

  const fetchMonthlyStats = useCallback(async () => {
    try {
      const now = new Date();
      const month = now.getMonth() + 1;
      const year = now.getFullYear();
      const response = await axios.get(`${API}/reports/monthly/${user.id}/${year}/${month}`);
      
      // Calculează statistici
      const attendances = response.data.attendances || [];
      const totalHours = attendances.reduce((sum, a) => sum + (a.hours_worked || 0), 0);
      const totalDays = attendances.length;
      
      // Estimare câștig pe baza salariului din profil sau default
      const hourlyRate = user.salary ? (user.salary / 168) : 24.1; // 168 ore/lună sau salariu minim
      const estimatedEarnings = totalHours * hourlyRate;
      
      setMonthlyStats({
        totalHours: Math.round(totalHours * 10) / 10,
        totalDays,
        estimatedEarnings: Math.round(estimatedEarnings),
        hourlyRate: Math.round(hourlyRate * 10) / 10,
        month: now.toLocaleString('ro-RO', { month: 'long' }),
        year
      });
    } catch (error) {
      console.error("Error fetching monthly stats:", error);
    }
  }, [user.id, user.salary]);

  const fetchActiveTripSheet = useCallback(async (tripId) => {
    try {
      const response = await axios.get(`${API}/trip-sheets/${tripId}/route`);
      setActiveTripSheet(response.data.trip);
      setTripRoute(response.data);
    } catch (error) {
      console.error("Error fetching trip sheet:", error);
    }
  }, []);

  // Main data fetching useEffect - runs on mount
  useEffect(() => {
    fetchStatus();
    fetchSites();
    fetchTools();
    fetchCompanyVehicles();
    fetchCompensatory();
    fetchOvertime();
    fetchMyTasks();
    fetchMonthlyStats();
    fetchMyNotifications();
    
    const timer = setInterval(() => {
      setCurrentTime(getCurrentTime());
    }, 1000);

    const notifInterval = setInterval(fetchMyNotifications, 30000);
    
    return () => { clearInterval(timer); clearInterval(notifInterval); };
  }, [fetchStatus, fetchSites, fetchTools, fetchCompanyVehicles, fetchCompensatory, fetchOvertime, fetchMyTasks, fetchMonthlyStats, fetchMyNotifications]);

  // Fetch active trip sheet when status changes
  useEffect(() => {
    if (status.attendance?.uses_company_car && status.attendance?.trip_sheet_id) {
      fetchActiveTripSheet(status.attendance.trip_sheet_id);
    } else {
      setActiveTripSheet(null);
      setTripRoute(null);
    }
  }, [status.attendance, fetchActiveTripSheet]);

  const handleMarkNotifRead = async (notifId) => {
    try {
      await axios.put(`${API}/notifications/${notifId}/read`);
      fetchMyNotifications();
    } catch (error) {
      console.error("Error marking notification read:", error);
    }
  };

  const handleStartTask = async (task) => {
    setLoading(true);
    try {
      await axios.post(`${API}/tasks/${task.id}/start`, {
        task_id: task.id,
        user_id: user.id,
        user_name: user.name
      });
      toast.success("Intervenție începută! Cronometrul a pornit.");
      setActiveTask(task);
      fetchMyTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare");
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async () => {
    if (!activeTask) return;
    setLoading(true);
    try {
      await axios.post(`${API}/tasks/${activeTask.id}/complete`, {
        task_id: activeTask.id,
        user_id: user.id,
        user_name: user.name,
        resolution_notes: taskResolutionNotes
      });
      toast.success("Tichet rezolvat cu succes!");
      setActiveTask(null);
      setTaskResolutionNotes("");
      setShowCompleteTaskDialog(false);
      fetchMyTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckin = async () => {
    // Validate site selection
    if (!selectedSite) {
      toast.error("Selectează un șantier");
      return;
    }
    
    // Validate company car data if selected
    if (usesCompanyCar) {
      if (!selectedVehicle) {
        toast.error("Selectează mașina de firmă");
        return;
      }
      if (!startKm || parseFloat(startKm) <= 0) {
        toast.error("Introdu kilometrajul de plecare");
        return;
      }
    }

    setLoading(true);
    try {
      await axios.post(`${API}/attendance/checkin`, {
        user_id: user.id,
        user_name: user.name,
        site_id: selectedSite,
        custom_work_point: null,
        tool_ids: selectedTools,
        location: null, // GPS disabled
        uses_company_car: usesCompanyCar,
        company_car_plate: selectedVehicle?.plate || null,
        company_car_name: selectedVehicle?.name || null,
        start_km: usesCompanyCar ? parseFloat(startKm) : null
      });
      
      const carMsg = usesCompanyCar ? ` 🚗 Traseu monitorizat (${selectedVehicle?.plate})` : "";
      toast.success(`PREZENT înregistrat!${carMsg}`);
      setShowCheckinDialog(false);
      setSelectedSite("");
      setSelectedTools([]);
      setUsesCompanyCar(false);
      setSelectedVehicle(null);
      setStartKm("");
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la înregistrare");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/attendance/checkout`, {
        user_id: user.id
      });
      
      toast.success(`Program încheiat! Ai lucrat ${response.data.hours_worked} ore.`);
      fetchStatus();
      fetchOvertime();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la încheiere program");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestBreak = async () => {
    if (!breakReason) {
      toast.error("Specifică motivul pauzei");
      return;
    }

    setLoading(true);
    try {
      const now = new Date();
      await axios.post(`${API}/breaks/request`, {
        user_id: user.id,
        user_name: user.name,
        start_time: now.toISOString(),
        duration_minutes: breakDuration,
        reason: breakReason
      });
      
      toast.success("Pauză solicitată! Dacă nu primești aprobare în 15 minute, se va aproba automat.", {
        duration: 5000
      });
      setShowBreakDialog(false);
      setBreakReason("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la solicitare pauză");
    } finally {
      setLoading(false);
    }
  };

  const handleDeferLunch = async (type) => {
    setLoading(true);
    try {
      await axios.post(`${API}/compensatory/defer-lunch`, {
        user_id: user.id,
        defer_type: type,
        date: new Date().toISOString()
      });
      
      const hours = type === "half" ? "0.5" : "1";
      toast.success(`Ai câștigat ${hours} ore compensatorii! Pauza ta va fi achitată.`);
      fetchCompensatory();
    } catch (error) {
      toast.error("Eroare la amânare pauză");
    } finally {
      setLoading(false);
    }
  };

  const handleEnableOvertime = async () => {
    try {
      await axios.post(`${API}/overtime/enable?user_id=${user.id}`);
      toast.success("Ore suplimentare activate! Orele după 16:00 vor fi contorizate.");
    } catch (error) {
      toast.error("Eroare la activare ore suplimentare");
    }
  };

  const handleRequestLeave = async () => {
    if (!leaveStartDate || !leaveEndDate) {
      toast.error("Selectează perioada concediului");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/leaves/request`, {
        user_id: user.id,
        user_name: user.name,
        type: leaveType,
        start_date: new Date(leaveStartDate).toISOString(),
        end_date: new Date(leaveEndDate).toISOString(),
        reason: leaveReason
      });
      
      toast.success(`Cerere trimisă! ${response.data.days_count} zile lucrătoare.`, { duration: 5000 });
      setShowLeaveDialog(false);
      setLeaveStartDate("");
      setLeaveEndDate("");
      setLeaveReason("");
      setLeaveDays(0);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la solicitare concediu");
    } finally {
      setLoading(false);
    }
  };

  // Calculate leave days when dates change
  useEffect(() => {
    if (leaveStartDate && leaveEndDate) {
      const start = new Date(leaveStartDate);
      const end = new Date(leaveEndDate);
      let days = 0;
      let current = new Date(start);
      
      while (current <= end) {
        if (current.getDay() !== 0 && current.getDay() !== 6) {
          days++;
        }
        current.setDate(current.getDate() + 1);
      }
      
      setLeaveDays(days);
    } else {
      setLeaveDays(0);
    }
  }, [leaveStartDate, leaveEndDate]);

  const handleEditAttendance = async (newTime) => {
    if (!status.auto_generated) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/attendance/edit`, {
        attendance_id: status.auto_generated.id,
        new_check_in: newTime
      });
      
      toast.success("Pontaj actualizat!");
      setShowEditDialog(false);
      fetchStatus();
    } catch (error) {
      toast.error("Eroare la actualizare pontaj");
    } finally {
      setLoading(false);
    }
  };

  const handleToolToggle = (toolId) => {
    setSelectedTools(prev => 
      prev.includes(toolId) 
        ? prev.filter(id => id !== toolId)
        : [...prev, toolId]
    );
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
    localStorage.removeItem('rememberMe');
    navigate('/login');
  };

  const formatTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <GlassLayout variant="worker" data-testid="worker-dashboard">
      {/* Header */}
      <div className="glass-header sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk, sans-serif'}}>e-Tesa</h1>
              <p className="text-xs sm:text-sm text-slate-600">{user.name}</p>
              {/* Buton mesaje sub numele angajatului */}
              <div className="mt-1">
                <WorkerMessenger 
                  userId={user.id}
                  userName={user.name}
                  companyId={companyId}
                />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button 
              onClick={() => setShowNotifDialog(true)}
              variant="outline"
              className="rounded-full px-2 relative border-teal-300 text-teal-700 hover:bg-teal-50"
              data-testid="worker-notifications-button"
            >
              <Bell className="w-4 h-4" />
              {unreadNotifCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                  {unreadNotifCount}
                </span>
              )}
            </Button>
            <Button 
              onClick={() => setShowMyAttendance(true)}
              className="rounded-full px-3 sm:px-5 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white shadow-md hover:shadow-lg transition-all"
              data-testid="my-attendance-button"
            >
              <CalendarDays className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline font-semibold">PONTAJUL MEU</span>
              <span className="sm:hidden font-semibold ml-1">Pontaj</span>
            </Button>
            <Button 
              onClick={() => setShowBenefitsModal(true)}
              variant="outline"
              className="rounded-full px-2 sm:px-4"
              data-testid="benefits-button"
            >
              <BookOpen className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">BENEFICII ȘI ÎNDATORIRI</span>
            </Button>
            <Button onClick={handleLogout} variant="outline" className="rounded-full px-3 sm:px-4" data-testid="logout-button">
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Ieșire</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8 max-w-4xl">
        {/* Current Time */}
        <div className="text-center mb-8">
          <p className="text-sm text-slate-600 mb-2">Ora curentă</p>
          <p className="text-5xl font-bold font-mono" style={{fontFamily: 'Space Grotesk, sans-serif'}}>{currentTime}</p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-green-500 to-teal-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Ore Compensatorii</p>
                  <p className="text-3xl font-bold mt-1">{compensatoryHours.toFixed(1)}</p>
                </div>
                <Award className="w-10 h-10 opacity-80" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-500 to-indigo-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Ore Suplimentare</p>
                  <p className="text-3xl font-bold mt-1">{overtimeHours.toFixed(1)}</p>
                </div>
                <Zap className="w-10 h-10 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Auto-generated Attendance Alert */}
        {status.auto_generated && !status.auto_generated.edited_by_worker && (
          <Alert className="mb-6 border-orange-300 bg-orange-50">
            <AlertCircle className="h-5 w-5 text-orange-600" />
            <AlertDescription className="text-base">
              <strong>Pontaj realizat automat</strong> de la ora {formatTime(status.auto_generated.check_in)}. 
              Dacă dorești să editezi ora de prezență,{" "}
              <button 
                onClick={() => setShowEditDialog(true)}
                className="text-orange-700 underline font-semibold"
              >
                dă click aici
              </button>
            </AlertDescription>
          </Alert>
        )}

        {/* Status Card */}
        <Card className="border-0 shadow-2xl mb-8 overflow-hidden" data-testid="status-card">
          <div className={`h-2 ${status.checked_in ? 'bg-gradient-to-r from-green-400 to-teal-500' : 'bg-gradient-to-r from-slate-300 to-slate-400'}`}></div>
          <CardHeader>
            <CardTitle className="text-2xl flex items-center" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              {status.checked_in ? (
                <>
                  <CheckCircle2 className="w-7 h-7 text-green-500 mr-3" />
                  EȘTI LA SERVICIU
                </>
              ) : (
                <>
                  <XCircle className="w-7 h-7 text-slate-400 mr-3" />
                  NU EȘTI LA SERVICIU
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {status.checked_in && status.attendance ? (
              <>
                <div className="p-4 bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl space-y-3">
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 text-teal-600 mr-2" />
                    <div>
                      <p className="text-xs text-slate-600">{status.attendance.custom_work_point ? 'Punct de Lucru' : 'Șantier'}</p>
                      <p className="font-semibold text-lg">{status.attendance.custom_work_point || status.attendance.site_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 text-blue-600 mr-2" />
                    <div>
                      <p className="text-xs text-slate-600">PREZENT/SOSIT</p>
                      <p className="font-mono font-semibold text-lg">{formatTime(status.attendance.check_in)}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-slate-600 mb-2">Unelte utilizate</p>
                    <div className="flex flex-wrap gap-2">
                      {status.attendance.tool_names?.map((tool) => (
                        <Badge key={tool} className="bg-white border border-teal-200 text-teal-700 text-sm">
                          <Wrench className="w-3 h-3 mr-1" />
                          {tool}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  {/* Company Car Trip Info */}
                  {status.attendance.uses_company_car && (
                    <div className="mt-4 p-4 bg-orange-50 rounded-xl border border-orange-200">
                      <div className="flex items-center mb-3">
                        <Car className="w-5 h-5 text-orange-600 mr-2" />
                        <span className="font-semibold text-orange-800">Traseu Mașină Firmă</span>
                        <Badge className="ml-2 bg-orange-500 text-white text-xs">
                          {status.attendance.company_car_plate}
                        </Badge>
                      </div>
                      
                      {activeTripSheet && (
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-orange-700">Km plecare:</span>
                            <span className="font-mono font-semibold">{activeTripSheet.start_km} km</span>
                          </div>
                          
                          {tripRoute?.route_points?.length > 0 && (
                            <div className="mt-2">
                              <p className="text-orange-700 mb-1">Puncte de colectare:</p>
                              {tripRoute.route_points.map((point) => (
                                <div key={point.worker_name} className="flex items-center text-xs py-1">
                                  <div className="w-5 h-5 rounded-full bg-orange-500 text-white flex items-center justify-center mr-2 text-xs">
                                    {tripRoute.route_points.indexOf(point) + 1}
                                  </div>
                                  <span>{point.worker_name} - {point.address}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          <p className="text-xs text-orange-600 mt-2">
                            * La final de program, vei completa km de sosire
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button 
                    onClick={handleCheckout} 
                    disabled={loading}
                    className="w-full h-14 bg-gradient-to-r from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700 text-white text-lg font-bold shadow-lg"
                    data-testid="checkout-button"
                  >
                    {loading ? "Se procesează..." : "ÎNCHEIERE PROGRAM"}
                  </Button>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      onClick={() => setShowBreakDialog(true)}
                      variant="outline"
                      className="h-12"
                      data-testid="request-break-button"
                    >
                      <Coffee className="w-4 h-4 mr-2" />
                      Solicită Pauză
                    </Button>
                    <Button
                      onClick={handleEnableOvertime}
                      variant="outline"
                      className="h-12"
                      data-testid="enable-overtime-button"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Ore Suplimentare
                    </Button>
                  </div>
                  
                  <Button
                    onClick={() => setShowLeaveDialog(true)}
                    variant="outline"
                    className="w-full h-12"
                    data-testid="request-leave-button"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Solicit Concediu
                  </Button>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      onClick={() => handleDeferLunch("half")}
                      variant="outline"
                      className="h-12 text-xs"
                      disabled={loading}
                    >
                      Amân Pauză (30 min)
                    </Button>
                    <Button
                      onClick={() => handleDeferLunch("full")}
                      variant="outline"
                      className="h-12 text-xs"
                      disabled={loading}
                    >
                      Amân Pauză (1 oră)
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <Button 
                onClick={() => setShowCheckinDialog(true)}
                className="w-full h-14 bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-600 hover:to-blue-700 text-white text-lg font-bold shadow-lg"
                data-testid="checkin-button"
              >
                PREZENT / SOSIT
              </Button>
            )}
          </CardContent>
        </Card>

        {/* My Tasks Section - Always Visible */}
        {myTasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled').length > 0 && (
          <Card className="border-0 shadow-lg mb-8 border-l-4 border-l-indigo-500" data-testid="worker-tasks-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-indigo-600" />
                  <span className="text-lg" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Sarcinile Mele</span>
                </div>
                <Badge className="bg-indigo-600">
                  {myTasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled').length} active
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {myTasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled').map((task) => (
                  <div 
                    key={task.id} 
                    className={`p-3 rounded-lg border ${
                      task.status === 'in_progress' ? 'bg-blue-100 border-blue-300' :
                      task.priority === 'urgent' ? 'bg-red-100 border-red-300' :
                      'bg-indigo-50 border-indigo-200'
                    }`}
                    data-testid={`worker-task-${task.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-sm">#{task.ticket_number}</span>
                          <span className="font-semibold text-sm">{task.title}</span>
                          {task.priority === 'urgent' && <Badge className="bg-red-600 text-xs">URGENT</Badge>}
                          {task.priority === 'high' && <Badge className="bg-orange-500 text-xs">Ridicată</Badge>}
                        </div>
                        <p className="text-xs text-slate-600">{task.description?.substring(0, 100)}{task.description?.length > 100 ? '...' : ''}</p>
                        <p className="text-xs text-slate-500 mt-1">📍 {task.site_name} {task.location_details && `- ${task.location_details}`}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2">
                      {task.status === 'open' && (
                        <Button
                          size="sm"
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-xs"
                          onClick={() => handleStartTask(task)}
                          disabled={loading}
                          data-testid={`start-task-${task.id}`}
                        >
                          <Play className="w-3 h-3 mr-1" />
                          Începe Intervenția
                        </Button>
                      )}
                      {task.status === 'in_progress' && (
                        <Button
                          size="sm"
                          className="flex-1 bg-green-600 hover:bg-green-700 text-xs"
                          onClick={() => {
                            setActiveTask(task);
                            setShowCompleteTaskDialog(true);
                          }}
                          disabled={loading}
                          data-testid={`complete-task-${task.id}`}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Finalizează
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Checkin Dialog */}
      <Dialog open={showCheckinDialog} onOpenChange={setShowCheckinDialog}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col" data-testid="checkin-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-2xl" style={{fontFamily: 'Space Grotesk, sans-serif'}}>PREZENT / SOSIT</DialogTitle>
            <DialogDescription>Selectează șantierul și uneltele utilizate</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            <div className="space-y-3">
              <Label className="text-base font-semibold">Selectează Șantierul</Label>
              <div className="space-y-2">
                {sites.map((site) => (
                  <button
                    key={site.id}
                    onClick={() => setSelectedSite(site.id)}
                    className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                      selectedSite === site.id
                        ? 'border-teal-500 bg-teal-100 shadow-md'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                    data-testid={`site-option-${site.id}`}
                  >
                    <p className="font-semibold text-sm">{site.name}</p>
                    <p className="text-xs text-slate-600">{site.address}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-base font-semibold">Selectează Uneltele <span className="text-slate-400 font-normal text-sm">(opțional)</span></Label>
              <div className="space-y-2">
                {tools.map((tool) => (
                  <div
                    key={tool.id}
                    className="flex items-center space-x-3 p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                  >
                    <Checkbox
                      id={`tool-${tool.id}`}
                      checked={selectedTools.includes(tool.id)}
                      onCheckedChange={() => handleToolToggle(tool.id)}
                      data-testid={`tool-checkbox-${tool.id}`}
                    />
                    <Label
                      htmlFor={`tool-${tool.id}`}
                      className="flex-1 cursor-pointer"
                    >
                      <p className="font-medium text-sm">{tool.name}</p>
                      {tool.description && <p className="text-xs text-slate-600">{tool.description}</p>}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Company Car Section */}
            <div className="space-y-3 p-3 rounded-xl bg-orange-50 border border-orange-200">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="company-car"
                  checked={usesCompanyCar}
                  onCheckedChange={(checked) => {
                    setUsesCompanyCar(checked);
                    if (!checked) {
                      setSelectedVehicle(null);
                      setStartKm("");
                    }
                  }}
                  data-testid="company-car-checkbox"
                />
                <Label htmlFor="company-car" className="flex items-center cursor-pointer">
                  <Car className="w-5 h-5 text-orange-600 mr-2" />
                  <span className="font-semibold text-orange-800">Merg cu mașina de firmă</span>
                </Label>
              </div>
              
              {usesCompanyCar && (
                <div className="space-y-3 mt-3 pl-6">
                  <div>
                    <Label className="text-sm text-orange-700">Selectează Mașina</Label>
                    <div className="space-y-2 mt-1">
                      {companyVehicles.map((vehicle) => (
                        <button
                          key={vehicle.plate || vehicle.id}
                          onClick={async () => {
                            setSelectedVehicle(vehicle);
                            // Auto-fetch last km
                            try {
                              const response = await axios.get(`${API}/vehicles/plate/${vehicle.plate}/last-km`);
                              setStartKm(response.data.last_km?.toString() || "");
                            } catch (err) {
                              console.error("Error fetching last km:", err);
                            }
                          }}
                          className={`w-full p-3 rounded-lg border text-left transition-all ${
                            selectedVehicle?.plate === vehicle.plate
                              ? 'border-orange-500 bg-orange-100'
                              : 'border-orange-200 bg-white hover:border-orange-300'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-semibold text-sm">{vehicle.plate}</p>
                              <p className="text-xs text-slate-600">{vehicle.name}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-teal-600 font-medium">{vehicle.current_km?.toLocaleString()} km</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {selectedVehicle && (
                    <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                      <p className="text-xs text-teal-700">Kilometraj automat din ultima cursă:</p>
                      <p className="text-lg font-bold text-teal-800">{startKm ? parseInt(startKm).toLocaleString() : '---'} km</p>
                    </div>
                  )}
                  
                  <Alert className="border-orange-300 bg-orange-100">
                    <Car className="h-4 w-4 text-orange-600" />
                    <AlertDescription className="text-xs text-orange-800">
                      Traseul va fi monitorizat automat. La final de tură, vei completa km de sosire.
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </div>
          </div>

          <div className="flex-shrink-0 pt-4 border-t">
            <Button 
              onClick={handleCheckin} 
              disabled={loading || !selectedSite || (usesCompanyCar && (!selectedVehicle || !startKm))}
              className="w-full h-12 bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-600 hover:to-blue-700 text-white font-semibold shadow-lg"
              data-testid="confirm-checkin-button"
            >
              {loading ? "Se procesează..." : usesCompanyCar ? "Confirmă + Pornește Traseu" : "Confirmă Prezența"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Break Request Dialog */}
      <Dialog open={showBreakDialog} onOpenChange={setShowBreakDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Solicită Pauză</DialogTitle>
          </DialogHeader>
          <Alert className="border-orange-300 bg-orange-50">
            <AlertCircle className="h-5 w-5 text-orange-600" />
            <AlertDescription>
              <strong>Important:</strong> Dacă nu primești acordul administratorului, pauza se va înregistra automat la 15 minute de la solicitare.
            </AlertDescription>
          </Alert>
          <div className="space-y-4">
            <div>
              <Label>Durată (minute)</Label>
              <Select value={breakDuration.toString()} onValueChange={(v) => setBreakDuration(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 minute</SelectItem>
                  <SelectItem value="30">30 minute</SelectItem>
                  <SelectItem value="45">45 minute</SelectItem>
                  <SelectItem value="60">60 minute</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Motiv</Label>
              <Input
                value={breakReason}
                onChange={(e) => setBreakReason(e.target.value)}
                placeholder="Motivul pauzei..."
              />
            </div>
            <Button
              onClick={handleRequestBreak}
              disabled={loading}
              className="w-full"
            >
              {loading ? "Se trimite..." : "Trimite Solicitare"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Attendance Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Editează Pontaj</DialogTitle>
            <DialogDescription>
              Pontajul a fost realizat automat. Specifică ora exactă la care ai ajuns.
            </DialogDescription>
          </DialogHeader>
          {status.auto_generated && (
            <div className="space-y-4">
              <div>
                <Label>Ora setată automat</Label>
                <Input value={formatTime(status.auto_generated.check_in)} disabled />
              </div>
              <div>
                <Label>Ora reală de sosire</Label>
                <Input
                  type="time"
                  onChange={(e) => {
                    const [hours, minutes] = e.target.value.split(':');
                    const date = new Date(status.auto_generated.check_in);
                    date.setHours(parseInt(hours), parseInt(minutes));
                    handleEditAttendance(date.toISOString());
                  }}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Leave Request Dialog */}
      <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-2xl" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Solicit Concediu</DialogTitle>
            <DialogDescription>Completează datele pentru cererea oficială de concediu</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            <Alert className="border-teal-300 bg-teal-50">
              <AlertCircle className="h-4 w-4 text-teal-600" />
              <AlertDescription className="text-sm">
                Se va genera automat o <strong>cerere oficială de concediu</strong> cu datele tale.
              </AlertDescription>
            </Alert>
            
            <div>
              <Label>Tip Concediu *</Label>
              <Select value={leaveType} onValueChange={setLeaveType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CO">CO - Concediu de Odihnă</SelectItem>
                  <SelectItem value="CM">CM - Concediu Medical</SelectItem>
                  <SelectItem value="CS">CS - Concediu Special (căsătorie, naștere, deces)</SelectItem>
                  <SelectItem value="FS">FS - Fără Salariu</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Început *</Label>
                <Input
                  type="date"
                  value={leaveStartDate}
                  onChange={(e) => setLeaveStartDate(e.target.value)}
                />
              </div>
              <div>
                <Label>Data Sfârșit *</Label>
                <Input
                  type="date"
                  value={leaveEndDate}
                  onChange={(e) => setLeaveEndDate(e.target.value)}
                />
              </div>
            </div>
            
            {leaveDays > 0 && (
              <Alert className="border-blue-300 bg-blue-50">
                <Calendar className="h-4 w-4 text-blue-600" />
                <AlertDescription>
                  <strong>{leaveDays} zile lucrătoare</strong> (exclude weekendurile)
                </AlertDescription>
              </Alert>
            )}
            
            <div>
              <Label>Motivul cererii</Label>
              <Input
                value={leaveReason}
                onChange={(e) => setLeaveReason(e.target.value)}
                placeholder="Ex: Vacanță în familie, evenimente personale..."
              />
              <p className="text-xs text-slate-500 mt-1">Opțional - va apărea în cererea oficială</p>
            </div>
            
            {/* Preview cerere */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-xs text-slate-500 mb-2 font-semibold">PREVIZUALIZARE CERERE:</p>
              <div className="text-sm space-y-1">
                <p><strong>Solicitant:</strong> {user.name}</p>
                <p><strong>Tip:</strong> {leaveType === 'CO' ? 'Concediu de Odihnă' : leaveType === 'CM' ? 'Concediu Medical' : leaveType === 'CS' ? 'Concediu Special' : 'Fără Salariu'}</p>
                {leaveStartDate && leaveEndDate && (
                  <p><strong>Perioada:</strong> {formatDateRO(leaveStartDate)} - {formatDateRO(leaveEndDate)}</p>
                )}
                {leaveDays > 0 && <p><strong>Zile:</strong> {leaveDays} zile lucrătoare</p>}
              </div>
            </div>
          </div>
          
          <div className="flex-shrink-0 pt-4 border-t">
            <Button
              onClick={handleRequestLeave}
              disabled={loading || !leaveStartDate || !leaveEndDate}
              className="w-full h-12 bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-600 hover:to-blue-700"
            >
              {loading ? "Se trimite cererea..." : "Trimite Cererea Oficială"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Complete Task Dialog */}
      <Dialog open={showCompleteTaskDialog} onOpenChange={setShowCompleteTaskDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-green-600" />
              Finalizare Tichet #{activeTask?.ticket_number}
            </DialogTitle>
            <DialogDescription>Marchează sarcina ca rezolvată</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {activeTask && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-semibold">{activeTask.title}</p>
                <p className="text-sm text-slate-600">{activeTask.site_name} - {activeTask.location_details}</p>
              </div>
            )}
            <div>
              <Label>Note despre rezolvare</Label>
              <Input
                value={taskResolutionNotes}
                onChange={(e) => setTaskResolutionNotes(e.target.value)}
                placeholder="Ce ai făcut pentru a rezolva problema..."
              />
            </div>
            <Button
              onClick={handleCompleteTask}
              disabled={loading}
              className="w-full h-12 bg-green-600 hover:bg-green-700"
            >
              {loading ? "Se procesează..." : "Marchează ca Rezolvat ✓"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Benefits Modal */}
      <BenefitsModal open={showBenefitsModal} onOpenChange={setShowBenefitsModal} />
      
      {/* Notifications Dialog */}
      <Dialog open={showNotifDialog} onOpenChange={setShowNotifDialog}>
        <DialogContent className="max-w-md max-h-[80vh]" data-testid="worker-notifications-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-teal-600" />
              Notificări
              {unreadNotifCount > 0 && <Badge className="bg-blue-500 text-white">{unreadNotifCount} necitite</Badge>}
            </DialogTitle>
            <DialogDescription>Notificările tale recente</DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-2">
            {myNotifications.length === 0 ? (
              <p className="text-slate-500 text-center py-6">Nicio notificare</p>
            ) : (
              myNotifications.map((notif) => (
                <div 
                  key={notif.id} 
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${notif.read ? 'bg-slate-50 border-slate-200 opacity-70' : 'bg-blue-50 border-blue-300 hover:bg-blue-100'}`}
                  onClick={() => { if (!notif.read) handleMarkNotifRead(notif.id); }}
                  data-testid={`notification-item-${notif.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm">{notif.title || 'Notificare'}</span>
                    {!notif.read && <Badge className="bg-blue-500 text-white text-xs">NOU</Badge>}
                  </div>
                  <p className="text-xs text-slate-600">{notif.message}</p>
                  <p className="text-xs text-slate-400 mt-1">{notif.created_at ? formatDateTimeRO(notif.created_at) : ''}</p>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* My Attendance Dialog */}
      <MyAttendanceDialog 
        open={showMyAttendance} 
        onOpenChange={setShowMyAttendance}
        userId={user.id}
        userName={user.name}
        companyId={companyId}
        userSalary={user.salary}
      />
    </GlassLayout>
  );
}